import React from 'react';
import Select from 'react-select';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../components/ConfirmModal';
import { DSBackLink, DSButton, DSBadge, DSCard, DSIcon, DSTextarea } from '../design-system/primitives';
import {
  ImpactBadge,
  TierBadge,
  AssigneePill,
  ActionBtn,
  IMPACT_CONFIG,
  TIER_NAMES,
  relativeTime
} from '../components/TicketUi';
import useMachineIssueDetail from '../hooks/useMachineIssueDetail';
import '../components/TicketUi.css';
import './MachineIssueDetail.css';

const REQUIRED_TIER_OPTIONS = [
  { value: 0, label: '0 - No Access' },
  { value: 2, label: '2 - Routine Maintenance' },
  { value: 3, label: '3 - Repair Tech' },
  { value: 4, label: '4 - Senior Tech' }
];

const IMPACT_OPTIONS = [
  { value: 'game_down', label: 'Game Down' },
  { value: 'tournament_risk', label: 'Tournament Risk' },
  { value: 'observation', label: 'Observation' }
];

function MachineIssueDetail() {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Callers that want "back" to return somewhere other than the general
  // ticket queue (e.g. the maintenance checklist this ticket was opened
  // from) pass returnTo/returnLabel via navigate state - see
  // MaintenanceRecordDetail.js.
  const backTo = location.state?.returnTo || '/machines/issues';
  // Label only - DSBackLink draws the arrow, so callers pass plain text.
  const backLabel = location.state?.returnLabel || 'Back to Tickets';
  const {
    isAdmin,
    techTier,
    issue,
    saving,
    loading,
    error,
    showTriage,
    setShowTriage,
    triageForm,
    setTriageForm,
    triageError,
    showEscalate,
    setShowEscalate,
    escalateForm,
    setEscalateForm,
    escalateError,
    showReassign,
    setShowReassign,
    reassignTo,
    setReassignTo,
    reassignError,
    showResolve,
    setShowResolve,
    resolveNote,
    setResolveNote,
    resolveError,
    noteText,
    setNoteText,
    noteError,
    showDeleteConfirm,
    setShowDeleteConfirm,
    deleting,
    showPromote,
    setShowPromote,
    promoteForm,
    setPromoteForm,
    promoteError,
    promoting,
    timelineEntries,
    assigneeOptions,
    selectedReassignOption,
    assigneeSelectStyles,
    needsTriage,
    qualifies,
    canTriage,
    canClaim,
    canEscalate,
    canResolve,
    canReopen,
    canReassign,
    canAddNote,
    canPromoteToKnownIssue,
    openTriage,
    submitTriage,
    handleClaim,
    openEscalate,
    submitEscalate,
    openResolve,
    submitResolve,
    handleReopen,
    openReassign,
    submitReassign,
    submitNote,
    openPromote,
    submitPromote,
    handleDelete
  } = useMachineIssueDetail(issueId);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusLabel = (statusValue) => {
    const statusMap = { open: 'Open', resolved: 'Resolved' };
    return statusMap[statusValue] || statusValue;
  };

  const handleDeleteClick = () => handleDelete(() => navigate(backTo));

  if (loading) {
    return <div className="machine-issue-detail ticket-ui"><div className="loading">Loading issue...</div></div>;
  }

  if (!issue) {
    return (
      <div className="machine-issue-detail ticket-ui">
        <div className="error-message">Issue not found.</div>
        <DSBackLink to={backTo}>{backLabel}</DSBackLink>
      </div>
    );
  }

  const noFormOpen = !showTriage && !showEscalate && !showReassign && !showResolve;

  return (
    <div className="machine-issue-detail ticket-ui">
      <div className="mid-header">
        <DSBackLink to={backTo}>{backLabel}</DSBackLink>
        <h1>{issue.machine_name}</h1>
        <p className="mid-header__desc">{issue.description}</p>
        <ImpactBadge impact={issue.impact} size="md" />
      </div>

      {error && <div className="error-message">{error}</div>}

      <DSCard
        className="mid-status-card"
        borderVariant="subtle"
        cornerVariant="md"
        footer={(
          <div className="mid-status-card__footer">
            <span>Reported by {issue.reporter_name || 'Anonymous'}</span>
            <span>{relativeTime(issue.created_at)}</span>
          </div>
        )}
      >
        <div className="mid-status-grid">
          <div>
            <p className="mid-status-grid__label">Status</p>
            <span className={`mid-status-pill mid-status-pill--${issue.status}`}>
              <span className="mid-status-pill__dot" />
              {getStatusLabel(issue.status)}
            </span>
          </div>
          <div>
            <p className="mid-status-grid__label">Assignee</p>
            <AssigneePill name={issue.assigned_to_name} />
          </div>
          <div>
            <p className="mid-status-grid__label">Required Tier</p>
            {needsTriage ? (
              <span className="mid-needs-triage"><DSIcon name="warning" /> Needs Triage</span>
            ) : (
              <div className="mid-tier-row">
                <TierBadge tier={issue.required_tech_tier} size="md" />
                <span className="mid-tier-row__name">{TIER_NAMES[issue.required_tech_tier]}</span>
              </div>
            )}
          </div>
          <div>
            <p className="mid-status-grid__label">Your Tier</p>
            <div className="mid-tier-row">
              <TierBadge tier={isAdmin ? 4 : techTier} size="md" />
              {!needsTriage && (
                <span className={`mid-qualified ${qualifies ? 'mid-qualified--yes' : 'mid-qualified--no'}`}>
                  {qualifies ? <><DSIcon name="check" /> Qualified</> : <><DSIcon name="lock" /> Not qualified</>}
                </span>
              )}
            </div>
          </div>
        </div>
        {issue.maintenance_record_id && (
          <div className="mid-maintenance-tag">
            <Link
              to={`/machines/maintenance/${issue.machine_id}`}
              state={{ returnTo: `/machine-issues/${issue.id}`, returnLabel: 'Back to Issue' }}
            >
              Found during maintenance
            </Link>
          </div>
        )}
        {issue.status === 'resolved' && (
          <div className="mid-resolved-info">
            Resolved by {issue.resolved_by_name} on {formatDate(issue.resolved_at)}
          </div>
        )}
      </DSCard>

      <div className="mid-actions">
        <p className="mid-section-label">Actions</p>
        <div className="mid-actions__buttons">
          {canClaim && (
            <ActionBtn
              label={issue.assigned_to_name ? `Take Over from ${issue.assigned_to_name}` : 'Claim Ticket'}
              variant={issue.assigned_to_name ? 'default' : 'primary'}
              onClick={handleClaim}
              disabled={saving}
            />
          )}
          {canTriage && !showTriage && (
            <ActionBtn label="Triage — Set Impact & Tier" variant="ghost" onClick={openTriage} disabled={saving} />
          )}
          {canEscalate && !showEscalate && (
            <ActionBtn label="Escalate to Higher Tier" variant="warning" onClick={openEscalate} disabled={saving} />
          )}
          {canResolve && !showResolve && (
            <ActionBtn label="Mark Resolved" variant="default" onClick={openResolve} disabled={saving} />
          )}
          {canReopen && (
            <ActionBtn label="Reopen Ticket" variant="ghost" onClick={handleReopen} disabled={saving} />
          )}
          {canReassign && !showReassign && (
            <ActionBtn label="Reassign to Another Tech" variant="ghost" onClick={openReassign} disabled={saving} />
          )}
          {canPromoteToKnownIssue && (
            <ActionBtn label="Promote to Known Issue" variant="ghost" onClick={openPromote} disabled={saving} />
          )}
          {isAdmin && (
            <ActionBtn label="Delete Ticket" variant="danger" onClick={() => setShowDeleteConfirm(true)} disabled={saving} />
          )}
        </div>
      </div>

      {showTriage && (
        <div className="mid-form">
          <p className="mid-form__title">Triage</p>

          <p className="mid-form__label">Impact</p>
          <div className="mid-form__impact-options">
            {IMPACT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`mid-form__impact-option ${triageForm.impact === option.value ? 'mid-form__impact-option--active' : ''}`}
                style={
                  triageForm.impact === option.value
                    ? { background: IMPACT_CONFIG[option.value].bg, borderColor: IMPACT_CONFIG[option.value].border, color: IMPACT_CONFIG[option.value].text }
                    : undefined
                }
                onClick={() => setTriageForm({ ...triageForm, impact: option.value })}
                disabled={saving}
              >
                <span className="mid-form__impact-dot" style={{ background: triageForm.impact === option.value ? IMPACT_CONFIG[option.value].dot: 'var(--ds-color-textDisabled)' }} />
                {option.label}
                {triageForm.impact === option.value && (
                  <span className="mid-form__impact-check" style={{ color: IMPACT_CONFIG[option.value].text }}><DSIcon name="check" /></span>
                )}
              </button>
            ))}
          </div>

          <p className="mid-form__label">Required Tech Tier</p>
          <div className="mid-form__tier-options">
            {REQUIRED_TIER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`mid-form__tier-option ${String(triageForm.requiredTechTier) === String(option.value) ? 'mid-form__tier-option--active' : ''}`}
                onClick={() => setTriageForm({ ...triageForm, requiredTechTier: option.value })}
                disabled={saving}
              >
                T{option.value}
              </button>
            ))}
          </div>

          <p className="mid-form__label">Note (required if this unassigns an under-qualified assignee)</p>
          <DSTextarea
            className="mid-form__textarea"
            value={triageForm.note}
            onChange={(e) => setTriageForm({ ...triageForm, note: e.target.value })}
            disabled={saving}
          />

          {triageError && <p className="mid-form__error">{triageError}</p>}

          <div className="mid-form__buttons">
            <button className="mid-form__apply" onClick={submitTriage} disabled={saving}>
              {saving ? 'Saving…' : 'Apply Triage'}
            </button>
            <button className="mid-form__cancel" onClick={() => setShowTriage(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showEscalate && (
        <div className="mid-form mid-form--warning">
          <p className="mid-form__title mid-form__title--warning">Escalate Ticket</p>
          <p className="mid-form__hint">Raises Required Tier above your own. Ticket returns to the work queue unassigned.</p>

          <p className="mid-form__label">New Required Tier</p>
          <div className="mid-form__tier-options">
            {REQUIRED_TIER_OPTIONS.filter((option) => option.value > (issue.required_tech_tier ?? 0)).map((option) => (
              <button
                key={option.value}
                type="button"
                className={`mid-form__tier-option ${String(escalateForm.requiredTechTier) === String(option.value) ? 'mid-form__tier-option--active' : ''}`}
                onClick={() => setEscalateForm({ ...escalateForm, requiredTechTier: option.value })}
                disabled={saving}
              >
                T{option.value}
              </button>
            ))}
          </div>

          <p className="mid-form__label mid-form__label--warning">Note Required</p>
          <DSTextarea
            className="mid-form__textarea"
            state="validation"
            value={escalateForm.note}
            onChange={(e) => setEscalateForm({ ...escalateForm, note: e.target.value })}
            placeholder="Describe what you found and why this needs a higher tier…"
            disabled={saving}
          />

          {escalateError && <p className="mid-form__error">{escalateError}</p>}

          <div className="mid-form__buttons">
            <button className="mid-form__apply mid-form__apply--warning" onClick={submitEscalate} disabled={saving}>
              {saving ? 'Escalating…' : 'Escalate'}
            </button>
            <button className="mid-form__cancel" onClick={() => setShowEscalate(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showResolve && (
        <div className="mid-form">
          <p className="mid-form__title">Resolve Ticket</p>

          <p className="mid-form__label">Note Required</p>
          <DSTextarea
            className="mid-form__textarea"
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            placeholder="Describe what was done to fix it…"
            disabled={saving}
          />

          {resolveError && <p className="mid-form__error">{resolveError}</p>}

          <div className="mid-form__buttons">
            <button className="mid-form__apply" onClick={submitResolve} disabled={saving}>
              {saving ? 'Resolving…' : 'Mark Resolved'}
            </button>
            <button className="mid-form__cancel" onClick={() => setShowResolve(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showReassign && (
        <div className="mid-form">
          <p className="mid-form__title">Reassign Ticket</p>

          <p className="mid-form__label">Assign To</p>
          <Select
            inputId="issue-reassign"
            options={assigneeOptions}
            value={selectedReassignOption}
            onChange={(option) => setReassignTo(option ? String(option.value) : '')}
            isSearchable
            isDisabled={saving}
            placeholder="Select a member"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={assigneeSelectStyles}
            noOptionsMessage={() => 'No eligible users found'}
            filterOption={(candidate, input) => {
              if (!input) return true;
              return candidate.data.searchText.includes(input.toLowerCase());
            }}
          />

          {reassignError && <p className="mid-form__error">{reassignError}</p>}

          <div className="mid-form__buttons">
            <button className="mid-form__apply" onClick={submitReassign} disabled={saving}>
              {saving ? 'Reassigning…' : 'Reassign'}
            </button>
            <button className="mid-form__cancel" onClick={() => setShowReassign(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mid-activity">
        <p className="mid-section-label">Activity</p>
        {timelineEntries.length === 0 ? (
          <p className="mid-activity__empty">No updates yet.</p>
        ) : (
          <div className="mid-activity__list">
            {timelineEntries.map((entry) => (
              <div key={`${entry.type}-${entry.id}`} className="mid-activity__item">
                <div className="mid-activity__rail">
                  <span className="mid-activity__dot" />
                  <span className="mid-activity__line" />
                </div>
                <div className="mid-activity__body">
                  <div className="mid-activity__meta">
                    <strong>{entry.user_name}</strong>
                    <span className="mid-activity__time">{formatDate(entry.created_at)}</span>
                  </div>
                  <div className="mid-activity__badges">
                    {entry.assigned_to_name && (
                      <DSBadge variant="neutral" size="sm">Assigned to {entry.assigned_to_name}</DSBadge>
                    )}
                    {entry.status && (
                      <span className="mid-activity__badge">Status: {getStatusLabel(entry.status)}</span>
                    )}
                  </div>
                  {entry.note && <p className="mid-activity__note">{entry.note}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {canAddNote && noFormOpen && (
        <div className="mid-add-note">
          <p className="mid-section-label">Add Note</p>
          <DSTextarea
            className="mid-form__textarea"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add an update or observation…"
            disabled={saving}
          />
          {noteError && <p className="mid-form__error">{noteError}</p>}
          <button className="mid-add-note__submit" onClick={submitNote} disabled={saving || !noteText.trim()}>
            Post Note
          </button>
        </div>
      )}

      {showDeleteConfirm && (
        <ConfirmModal
          title="Delete Ticket?"
          message="This permanently deletes the ticket and its activity history. This cannot be undone."
          confirmLabel="Delete"
          danger
          submitting={deleting}
          onConfirm={handleDeleteClick}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {showPromote && (
        <div className="modal-overlay" onClick={() => setShowPromote(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPromote(false)}><DSIcon name="x" /></button>
            <h2>Promote to Known Issue</h2>
            <form onSubmit={submitPromote} className="edit-form">
              <DSTextarea label="Description:"
                  value={promoteForm.description}
                  onChange={(e) => setPromoteForm({ ...promoteForm, description: e.target.value })}
                  rows="3"
                  placeholder="e.g., Left orbit rubber comes loose periodically"
                  required />
              <DSTextarea label="Guidance (optional):"
                  value={promoteForm.guidanceText}
                  onChange={(e) => setPromoteForm({ ...promoteForm, guidanceText: e.target.value })}
                  rows="3"
                  placeholder="What should a reporter/tech do if they see this?" />
              {promoteError && <div className="error-message">{promoteError}</div>}
              <div className="modal-actions">
                <DSButton type="submit" appearance="solid" tone="primary" disabled={promoting}>
                  {promoting ? 'Saving...' : 'Promote to Known Issue'}
                </DSButton>
                <DSButton type="button" onClick={() => setShowPromote(false)} appearance="outline" tone="neutral">
                  Cancel
                </DSButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MachineIssueDetail;
